import { useEffect, useRef, useState, type ReactNode } from 'react'
import { NavLink, Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { api, type Me, useCurrentCompany, useCurrentPartner, useMe, useRefreshMe, useSelectCompany, useSelectPartner } from '../lib'
import { Loading } from './ui'
import { SIDE_HOME, SIDE_TAG, heldSides, setSide, sideOfPath, useSide, type Side } from './side'
import { current } from './paths'
import { StaffApp } from '../features/staff/app/StaffApp'
import { ViewAsBar } from '../features/staff/ViewAs'
import { LogoTile } from './logo'
import { usePartnersOpen } from '../features/card/PartnersPage'

type Area = 'public' | Side

type NavItem = { to: string; label: string; end?: boolean }

// One row at 1280: five links at most. Everything else is in the menu under the email.
const NAV: Record<Area, NavItem[]> = {
  public: [{ to: '/directory', label: 'Partners' }],
  company: [
    { to: '/', label: 'Overview', end: true },
    { to: '/directory', label: 'Partners' },
    { to: '/invoices', label: 'Receipts' },
    { to: '/redeem', label: 'Redeem' },
    { to: '/team', label: 'Team' },
  ],
  // CHANGE-007: a partner sets up once (spend, points, how they pay) and has no per-customer or per-redemption
  // pages: no Customers, no link Requests, no Credits (cash rebates only in v1).
  partner: [
    { to: '/partner', label: 'Home', end: true },
    { to: '/partner/spend', label: 'Spend' },
    { to: '/partner/rules', label: 'Points' },
    { to: '/partner/bills', label: 'Bills' },
    { to: '/partner/team', label: 'Team' },
  ],
  // CHANGE-024: Home holds stuck work (claim cases, failed jobs, receipts, link requests) and the live feed;
  // the search box sits after these.
  staff: [
    { to: '/staff', label: 'Staff', end: true },   // CHANGE-070: the staff app has its own rail (features/staff/app/nav.ts)
  ],
}

// Under the email: pages that are not part of the daily nav. No page is deleted.
const MORE: Record<Area, NavItem[]> = {
  public: [],
  company: [
    { to: '/profile', label: 'Profile' },
    { to: '/claims', label: 'Partner accounts' },
    { to: '/card', label: 'Request early access' },
  ],
  // Billing, Company details and Terms were reachable only from the setup steps, which go away once live.
  partner: [
    { to: '/partner/profile', label: 'Profile' },
    { to: '/partner/button', label: 'Website button' },
    { to: '/partner/billing', label: 'Billing' },
    { to: '/partner/verify', label: 'Company details' },
    { to: '/partner/review', label: 'Terms' },
    { to: '/directory', label: 'Directory' },
    { to: '/partner/agent', label: 'Agent access' },
  ],
  staff: [
  ],
}

// Paths in an area that work without being signed in (the page itself explains what to do).
const OPEN_PATHS = ['/partner-invite/', '/claim/', '/invite/', '/login']

// CHANGE-049 (founder, 2026-10-31: "you should hit request a card, sign in, then go to the card application"): with the
// card-only switch on, the company side is these pages and nothing else, for staff too (staff see the rest by viewing as
// a company). CHANGE-071 (founder, 2026-11-04: "we don't have any partners anymore"): the partner side is for staff
// only. The server closes the rest of the rewards and partner APIs (pr/core/appmode.py rewards_route).
const CARD_MODE_PAGES = ['/card', '/profile', '/team', '/new', '/join', '/invite', '/login', '/partners']
// Signed out, the card-only app opens on a plain /login (founder, 2026-11-04: no ?next= in the address bar). Sign-in
// lands on / , which sends a card-only user to the card request anyway.
const CARD_APPLY_LOGIN = '/login'
const under = (pathname: string, p: string) => pathname === p || pathname.startsWith(`${p}/`)

function cardModeOpen(pathname: string, me: Me): boolean {
  if (under(pathname, '/staff')) return true
  if (under(pathname, '/partner') || under(pathname, '/partner-invite') || pathname === '/agent/connect') return me.is_staff
  return CARD_MODE_PAGES.some((p) => under(pathname, p))
}

// Browser tab title: the nav entry the page sits under (longest match), else just the product name.
function titleOf(area: Area, pathname: string): string {
  const hit = [...NAV[area], ...MORE[area]]
    .filter((n) => pathname === n.to || (!n.end && pathname.startsWith(`${n.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0]
  const where = area === 'staff' ? 'Staff' : area === 'partner' ? 'Partner portal' : null
  // Tab titles end with a single "| ernd" (ernd.io copy rules).
  if (pathname.startsWith('/directory/')) return 'Partner offer | ernd'
  if (/^\/staff\/(company|partner|person|file|charge|calc|payout|bill|record|go)\//.test(pathname)) return 'Staff | ernd'
  if (pathname === '/redemptions') return 'Payouts | ernd'
  const page = hit?.label ? (where ? `${hit.label}, ${where.toLowerCase()}` : hit.label) : where
  return page ? `${page} | ernd` : 'ernd'
}

export function Shell() {
  const { pathname, search } = useLocation()
  const { me, isLoading } = useMe()
  const signOut = useSignOut()
  const partnersOpen = usePartnersOpen()   // CHANGE-073: after the card request is sent
  const area: Area = sideOfPath(pathname) ?? 'public'
  const needsUser = area !== 'public' && !OPEN_PATHS.some((p) => pathname.startsWith(p))
  // Shared pages (/directory, /agent/connect) show the nav of the side the user chose; only the switcher changes it.
  const { side } = useSide()
  const navArea: Area = side ?? 'public'
  useEffect(() => {
    document.title = titleOf(navArea, pathname)
  }, [navArea, pathname])

  // On a narrow screen the nav scrolls sideways; keep the current page's item in view.
  const navRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const nav = navRef.current
    const active = nav?.querySelector<HTMLElement>('a.active')
    if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return
    const a = active.getBoundingClientRect()
    const n = nav.getBoundingClientRect()
    if (a.left < n.left || a.right > n.right) nav.scrollLeft += a.left - n.left - (n.width - a.width) / 2
  }, [navArea, pathname])

  // CHANGE-card (founder, 2026-10-27: "they just need to submit the application"): someone who came only for the
  // card sees the card pages in a minimal shell, never the rewards app; the same shell holds the card pages for anyone
  // with no company yet, and while /api/me loads on them (so nothing of the rewards app flashes).
  const onCard = pathname === '/card' || pathname.startsWith('/card/')
  // CARD_ONLY_APP (founder, 2026-10-27: "It should just be the credit card application inside the app for now"):
  // signed out, the home page and the partner directory go to sign-in for the card; signed in, everyone but staff
  // sees the card request only.
  const cardMode = !!me?.features?.card_only_app
  const publicPage = pathname === '/partners' || pathname.startsWith('/directory') || pathname.startsWith('/start')
  if (cardMode && me && !me.is_staff && (under(pathname, '/partner') || under(pathname, '/partner-invite')))
    return <Navigate to="/" replace />
  if (cardMode && me && !me.user && (publicPage || pathname.startsWith('/claim/') || (area === 'company' && !cardModeOpen(pathname, me))))
    return <Navigate to={CARD_APPLY_LOGIN} replace />
  if (cardMode && me?.user && !me.view_as && !cardModeOpen(pathname, me)) return <Navigate to="/card" replace />
  // The card's own bar on the company side (and its shared pages) while the app is card-only.
  const cardBar = cardMode && !me?.view_as && area !== 'staff' && area !== 'partner' && pathname !== '/login'
  const cardShell = !cardBar && onCard && (isLoading || !me?.user || !!me.card_only || me.companies.length === 0)
  if (!cardMode && me?.card_only && area === 'company' && !onCard) return <Navigate to="/card" replace />
  // CHANGE-070: staff get the staff app's own frame. Signed out or not staff falls through to the prompts below.
  if (area === 'staff' && me?.user && me.is_staff) return <StaffApp />

  let body = <Outlet />
  if (needsUser && isLoading) body = <Loading />
  else if (publicPage && isLoading) body = <Loading />          // the mode isn't known yet: don't flash the directory
  else if (needsUser && !me?.user) body = <SignInPrompt next={pathname + search} />
  else if (area === 'staff' && me?.user && !me.is_staff) body = <NoAccess what="the staff console" staff email={me.user.email} next={pathname} />

  return (
    // /login stands on the dark stage like ernd.io's hero.
    <div className={`shell area-${area}${me?.view_as ? ' viewing-as' : ''}${pathname === '/login' ? ' shell-stage' : ''}`}>
      {cardBar ? (
        // Card-only: the wordmark, the switcher for someone on both sides, and one menu.
        <header className="topbar topbar-card" data-testid="card-shell">
          <span className="topbar-card-left">
            <Link to="/card" className="brand" data-testid="web-brand"><Wordmark /></Link>
            {me?.companies.length && me.partners.length ? <SideSwitcher side="company" /> : null}
            {partnersOpen ? <Link to="/partners" className="topbar-card-out" data-testid="card-nav-partners">Partners</Link> : null}
          </span>
          <UserMenu area="company" card />
        </header>
      ) : cardShell ? (
        // The card flow alone: the wordmark, who is signed in, and Sign out.
        <header className="topbar topbar-card" data-testid="card-shell">
          <Link to="/card" className="brand" data-testid="web-brand"><Wordmark /></Link>
          {me?.user ? (
            <span className="topbar-card-user">
              <span className="topbar-card-email" data-testid="card-shell-email">{me.user.email}</span>
              <button type="button" className="topbar-card-out" data-testid="card-shell-sign-out" onClick={signOut}>Sign out</button>
            </span>
          ) : null}
        </header>
      ) : pathname === '/login' ? (
        // Sign in, as on ernd.io: the wordmark and the way back to the site, nothing else.
        <header className="topbar topbar-login">
          <Link to="/" className="brand" data-testid="web-brand"><Wordmark /></Link>
          <a className="topbar-back" href="https://ernd.io">Back to ernd.io</a>
        </header>
      ) : (
        <header className="topbar">
          <Link to={side ? SIDE_HOME[side] : '/'} className="brand" data-testid="web-brand">
            <Wordmark />
          </Link>
          {me?.view_as ? <ViewAsBar /> : <SideSwitcher side={side} />}
          <nav className="nav" aria-label="Main" ref={navRef}>
            {(isLoading || (cardMode && navArea === 'public') ? [] : NAV[navArea]).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`web-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <UserMenu area={navArea} />
        </header>
      )}
      <main className="main">{body}</main>
      {(navArea === 'company' || navArea === 'partner') && me?.user && pathname !== '/login' && !cardShell && !cardBar ? <Footer /> : null}
    </div>
  )
}

// The ernd wordmark, as on ernd.io since Malcolm's restyle (2026-10-27): "ernd" set live in ABC Diatype medium, slightly
// tight, in the text colour. The older outlined Fraunces mark is gone from the site, so it's gone here too.
export function Wordmark() {
  return <span className="wordmark" role="img" aria-label="ernd">ernd</span>
}

// Where a signed-in visit to /login goes: the same rule as the server (pr/login/service.py landing). A deep link
// wins; an area root, or none, means the side asked for, else a side the user belongs to.
export function landingFor(me: Me, next: string | null): string {
  // Same rule as the server (pr/login/service.py landing): a deep link wins, query and all; '/?utm_source=x'
  // still asks for the company side. An old /app/... link is read as its new path first (app/paths.ts).
  next = current(next)
  const [rawPath, ...rest] = (next ?? '').split('?')
  const query = rest.length ? rest.join('?') : ''
  const path = rawPath ? rawPath.replace(/\/+$/, '') || '/' : ''
  if (path && !['/', '/partner', '/staff'].includes(path)) return next as string
  const asked = path as '/' | '/partner' | '/staff' | ''
  const held: string[] = heldSides(me).map((s) => SIDE_HOME[s])
  // a new user who asked for the partner side goes straight to making one (CHANGE-038)
  const home = asked === '/partner' && !held.length ? '/partner/new' : asked
  if (asked && (held.includes(asked) || !held.length)) return query ? `${home}?${query}` : home
  return held[0] ?? '/'
}

/** A small pop-over menu. Closes on a click outside, Esc, or any click inside. */
function Menu({ trigger, children, testid, className }: { trigger: ReactNode; children: ReactNode; testid: string; className?: string }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const out = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false) }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', out)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', out); document.removeEventListener('keydown', esc) }
  }, [open])
  return (
    <div className={`menu ${className ?? ''}`} ref={box}>
      <button type="button" className="menu-button" aria-haspopup="menu" aria-expanded={open} data-testid={testid} onClick={() => setOpen(!open)}>
        {trigger}
      </button>
      {open && <div className="menu-list" role="menu" onClick={() => setOpen(false)}>{children}</div>}
    </div>
  )
}

// The one switcher: where you are (a name and a plain tag), and a menu of every place you can go. The tag (Company,
// Partner) shows to everyone, so a partner always sees they are on the partner side (CHANGE-041).
function SideSwitcher({ side }: { side: Side | null }) {
  const { me } = useMe()
  const company = useCurrentCompany()
  const partner = useCurrentPartner()
  const selectCompany = useSelectCompany()
  const selectPartner = useSelectPartner()
  const navigate = useNavigate()
  if (!me?.user || !side) return null
  const name = side === 'company' ? company?.name : side === 'partner' ? partner?.name : 'ernd'
  const logo = side === 'company' ? company?.logo_url : side === 'partner' ? partner?.logo_url : null
  if (!name) return null
  return (
    <Menu
      testid="web-side-switch"
      className="side-switch"
      trigger={
        <>
          {side !== 'staff' && <LogoTile name={name} url={logo} testid="web-side-logo" />}
          <span className="side-name" data-testid="web-side-name">{name}</span>
          <span className={`side-tag side-${side}`} data-testid="web-side-tag">{SIDE_TAG[side]}</span>
          <span className="chev" aria-hidden="true" />
        </>
      }
    >
      {me.companies.map((c) => (
        <button key={c.id} type="button" role="menuitem" data-testid={`web-side-company-${c.id}`}
          onClick={() => { selectCompany(c.id); setSide('company'); navigate(me.features?.card_only_app ? '/card' : '/') }}>
          <span className="side-item"><LogoTile name={c.name} url={c.logo_url} /><span>{c.name}</span></span><span className="side-tag side-company">Company</span>
        </button>
      ))}
      {me.partners.map((p) => (
        <button key={p.id} type="button" role="menuitem" data-testid={`web-side-partner-${p.id}`}
          onClick={() => { selectPartner(p.id); setSide('partner'); navigate('/partner') }}>
          <span className="side-item"><LogoTile name={p.name} url={p.logo_url} /><span>{p.name}</span></span><span className="side-tag side-partner">Partner</span>
        </button>
      ))}
      {me.is_staff && (
        <button type="button" role="menuitem" data-testid="web-side-staff" onClick={() => { setSide('staff'); navigate('/staff') }}>
          <span>ernd</span><span className="side-tag side-staff">Staff</span>
        </button>
      )}
    </Menu>
  )
}

function useSignOut() {
  const refresh = useRefreshMe()
  const navigate = useNavigate()
  return async () => {
    try {
      await api('POST', '/auth/logout', {})
    } finally {
      await refresh()
      navigate('/')
    }
  }
}

// The member app's footer, as on ernd.io: the wordmark, the year, Terms, Privacy and Sign out.
function Footer() {
  const signOut = useSignOut()
  return (
    <footer className="e-footer e-stage">
      <div className="e-footer-brand"><Wordmark /><span>© 2026 ernd</span></div>
      <nav className="e-footer-links" aria-label="Footer">
        <a href="https://ernd.io/terms">Terms</a>
        <a href="https://ernd.io/privacy">Privacy</a>
        <button type="button" className="e-footer-signout" onClick={signOut} data-testid="web-footer-sign-out">Sign out</button>
      </nav>
    </footer>
  )
}

function UserMenu({ area, card }: { area: Area; card?: boolean }) {
  const { me } = useMe()
  const partner = useCurrentPartner()
  const signOut = useSignOut()
  // CHANGE-042: Profile is for after set-up; a partner still setting up does not see it.
  const more = card
    // CHANGE-049, card-only: Profile and Team once there is a company, the staff console for staff, nothing else.
    ? [...(me?.companies.length ? [{ to: '/profile', label: 'Profile' }, { to: '/team', label: 'Team' }] : []),
       ...(me?.is_staff ? [{ to: '/staff', label: 'Staff console' }] : [])]
    : MORE[area].filter((n) => n.to !== '/partner/profile' || (partner?.status && partner.status !== 'draft'))
      .filter((n) => n.to !== '/card' || me?.features?.card)   // CHANGE-card: CARD_APPLICATIONS_ENABLED
  if (!me?.user) {
    return (
      <div className="user">
        <Link to="/login" className="button" data-testid="web-sign-in">
          Sign in
        </Link>
      </div>
    )
  }
  return (
    <Menu
      testid="web-user-menu"
      className="user"
      trigger={
        <>
          <span className="user-email" data-testid="web-user-email">{me.user.email}</span>
          <span className="user-label">Menu</span>
          <svg className="user-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          <span className="chev" aria-hidden="true" />
        </>
      }
    >
      {more.map((n) => (
        <Link key={n.to} to={n.to} role="menuitem" data-testid={`web-more-${n.label.toLowerCase().replace(/\s+/g, '-')}`}>{n.label}</Link>
      ))}
      {me.partners.length === 0 && area !== 'staff' && !card && (
        <Link to="/partner/new" role="menuitem" data-testid="web-become-partner">Become a partner</Link>
      )}
      <button
        type="button"
        role="menuitem"
        data-testid="web-sign-out"
        onClick={signOut}
      >
        Sign out
      </button>
    </Menu>
  )
}

function SignInPrompt({ next }: { next: string }) {
  return (
    <div className="page narrow" data-testid="web-sign-in-needed">
      <h1>Sign in to continue</h1>
      <p className="muted">This page is for signed-in users.</p>
      <p>
        <Link className={next.startsWith('/staff') ? 'button staff-outlined' : 'button primary'} to={`/login?next=${encodeURIComponent(next)}`} data-testid="web-sign-in-link">
          Sign in
        </Link>
      </p>
    </div>
  )
}

function NoAccess({ what, staff, email, next }: { what: string; staff?: boolean; email?: string; next?: string }) {
  if (!staff) {
    return (
      <div className="page narrow" data-testid="web-no-access">
        <h1>No access</h1>
        <p className="muted">Your account cannot open {what}.</p>
      </div>
    )
  }
  // Staff pages: say who is signed in and how a staff member gets in (pr/auth/api.py STAFF_ISSUERS).
  async function switchAccount() {
    try {
      await api('POST', '/auth/logout')
    } finally {
      window.location.assign(`/login?next=${encodeURIComponent(next || '/staff')}`)
    }
  }
  return (
    <div className="page narrow" data-testid="web-no-access">
      <h1>No access</h1>
      <p className="muted">{email ? <>{email} is not on the staff list.</> : <>This account is not on the staff list.</>}</p>
      <p className="muted">Sign in with your staff email. Google, Microsoft or an email link all work.</p>
      <p>
        <button type="button" className="button staff-outlined" onClick={switchAccount} data-testid="web-no-access-switch">
          Sign in again
        </button>
      </p>
    </div>
  )
}
